import { FC, useEffect, useRef, useState } from "react";

import { useDebounce } from "ui";
import FilterRow from "../components/generic-table/filter-row";

import { SortOptions } from "../components/generic-table/generic-table";
import { Nullable, useVehicleTypeControllerFindMany } from "service";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import VehicleTypesTable from "./vehicle-type-table";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { Box, Tabs, Tab } from "@mui/material";
import { Add } from "@mui/icons-material";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import HeaderStandardActions from "../../../shared/components/header-items/HeaderStandardActions";
import { usePersistedSearchParams } from "../../../hooks/use-persisted-search-params";
import EmptyState, {
  noResultsEmptyState,
} from "@/shared/components/EmptyState";
import { useListEmptyState } from "@/shared/hooks/useListEmptyState";

const VehicleTypesPage: FC = () => {
  const [params, setSearchParams] = useSearchParams();
  const [page, setPage] = useState<number>(
    params.get("page") ? parseInt(params.get("page") as string) : 1,
  );
  const [search, setSearch] = useState<string>(params.get("search") || "");
  const [limit, setLimit] = useState<number>(
    params.get("limit") ? parseInt(params.get("limit") as string) : 50,
  );

  // Persist search params for breadcrumb navigation
  usePersistedSearchParams();

  const [tabVal, setTabVal] = useState<number>(1);

  const { debounce } = useDebounce(475);
  const navigate = useNavigate();

  const onSearch = (searchValue: string) => {
    debounce(() => {
      setSearch(searchValue);
      // Reset to page 1 when search changes
      setPage(1);
      // Note: FilterRow already updates the 'search' param in the URL
    });
  };

  // Track previous values to avoid unnecessary URL updates on mount
  const prevPageRef = useRef(page);
  const prevLimitRef = useRef(limit);

  // Sync page and limit changes to URL (only when they actually change)
  useEffect(() => {
    // Skip if values haven't changed from their previous state
    if (prevPageRef.current === page && prevLimitRef.current === limit) {
      return;
    }
    prevPageRef.current = page;
    prevLimitRef.current = limit;

    setSearchParams((prev) => {
      if (page > 1) {
        prev.set("page", page.toString());
      } else {
        prev.delete("page");
      }
      if (limit !== 50) {
        prev.set("limit", limit.toString());
      } else {
        prev.delete("limit");
      }
      return prev;
    });
  }, [page, limit, setSearchParams]);

  const [sortOptions, setSortOptions] = useState<Nullable<SortOptions>>(
    params.get("sortBy")
      ? {
          sortBy: params.get("sortBy") || "",
          direction: (params.get("direction") || "") as "asc" | "desc",
        }
      : null,
  );

  const { isLoading, isError, data, refetch } =
    useVehicleTypeControllerFindMany({
      queryParams: {
        limit,
        search,
        page,
        sort: sortOptions?.sortBy as "typeName" | "travelSpeed" | "lastUpdated",
        order: sortOptions?.direction,
      },
    });

  const handleChange = (value: number) => {
    setTabVal(value);
    if (value === 0) {
      navigate(AdRoutes.fleetManagement);
    }
  };

  const hasActiveFilters = Boolean(search);
  const { isEmpty, isTrueEmpty } = useListEmptyState(
    isLoading,
    data?.metaData.count ?? 0,
    hasActiveFilters,
    isError,
  );

  const clearFilters = () => {
    setSearch("");
    setPage(1);
    setSearchParams((prev) => {
      prev.delete("search");
      prev.delete("page");
      return prev;
    });
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Fleet Management",
                url: AdRoutes.fleetManagement,
              },
            ]}
          />
          <HeaderItemsRight>
            <HeaderStandardActions
              onRefresh={isTrueEmpty ? undefined : refetch}
              onAdd={() => navigate(AdRoutes.addVehicleType)}
              addButtonText="Add Vehicle Type"
            />
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box
        sx={{
          marginTop: "25px",
          marginBottom: "25px",
          borderBottom: "1px solid #fff",
        }}
      >
        <Tabs
          aria-label="Select Vehicle Types"
          selectionFollowsFocus
          value={tabVal}
          onChange={(_e, value) => handleChange(value)}
        >
          <Tab label="Vehicles" value={0} sx={{ minWidth: "200px" }} />
          <Tab label="Vehicle Types" value={1} sx={{ minWidth: "200px" }} />
        </Tabs>
      </Box>
      {!isTrueEmpty && (
        <FilterRow
          tablePaginationProps={{
            limit,
            page,
            isLoading: isLoading,
            count: data?.metaData.count ?? 10,
            setToPage: setPage,
            setLimit,
          }}
          search={search}
          onSearchChanged={onSearch}
        />
      )}
      {isTrueEmpty ? (
        <Box mt="20px">
          <EmptyState
            title="No Vehicle Types yet"
            description="New vehicle types will appear here once you create them"
            actionLabel="Add Vehicle Type"
            actionStartIcon={<Add />}
            onAction={() => navigate(AdRoutes.addVehicleType)}
          />
        </Box>
      ) : isEmpty ? (
        <Box mt="20px">
          <EmptyState {...noResultsEmptyState("vehicle types", clearFilters)} />
        </Box>
      ) : (
        <VehicleTypesTable
          data={data?.data}
          sortOptions={sortOptions}
          sortOptionsChange={setSortOptions}
          isLoading={isLoading}
        />
      )}
    </DashboardContentLayout>
  );
};

export default VehicleTypesPage;
